/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.37.0
 * @since 1.37.0
 */

import { useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import { LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { formatNumberToPhone } from '@components/common/PhoneMask/formatNumberToPhone';
import type { ISimpleTableColumns, ISimpleTableData } from '@components/SimpleTable/SimpleTable';
import {
    makeSelectTranslationWizardChangeRole,
} from '@modules/internationalization/intSelectors';
import { IWizardChangeRoleTranslation } from '@modules/internationalization/types/wizards/changeRole';
import { WizardStepConfirmationRemovedItemStyled } from '@modules/wizard/components/styled/WizardConfirmationStyled';
import WizardConfirmationStep from '@modules/wizard/components/WizardConfirmationStep';
import type { IWizardSummaryParam } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportStorageSaveAction } from '@modules/wizard/wizardActions';
import type { IRootReducer } from '@reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';

import type { TWizardChangeRoleSetParamsPayload } from '../types/wizardChangeRoleActionTypes';
import { wizardChangeRoleSetParamsAction, wizardChangeRoleStartValidateAction } from '../wizardChangeRoleActions';
import { EWizardChangeRoleFieldList, EWizardChangeRoleSteps } from '../wizardChangeRoleConstant';
import type { IWizardChangeRoleOrderSubscriber, IWizardChangeRoleReducer } from '../wizardChangeRoleReducer';
import {
    makeSelectWizardChangeRoleOrderResult,
    makeSelectWizardChangeRoleRemovedSubscribers,
    makeSelectWizardChangeRoleSelectedAccessLevel,
} from '../wizardChangeRoleSelectors';

const WizardChangeRoleConfirmation = (props: IConnectedProps & IDispatchProps) => {
    const {
        selectedAccessLevel,
        orderResult,
        removedSubscribers,
        translation,

        setParams,
        startValidate,
        storageSave,
    } = props;
    const stepTranslations = translation[EWizardChangeRoleSteps.StepConfirmation];
    const summaryTranslations = translation.wizardSummary;

    const [tableData, setTableData] = useState<ISimpleTableData[]>([]);
    const [tableColumns, setTableColumns] = useState<ISimpleTableColumns[]>([]);
    const [summaryParams, setSummaryParams] = useState<IWizardSummaryParam[]>([]);
    const [subsCount, setSubsCount] = useState(0);

    const removeHandler = useCallback(
        (id: number) => {
            setParams({
                removedSubscribers: removedSubscribers.concat(id),
            });
        },
        [setParams, removedSubscribers],
    );

    const recoverHandler = useCallback(
        (id: number) => {
            setParams({
                removedSubscribers: [...removedSubscribers.filter((item) => item !== id)],
            });
        },
        [setParams, removedSubscribers],
    );

    useEffect(() => {
        setTableData(
            orderResult.orderSubs.map((item: IWizardChangeRoleOrderSubscriber) => ({
                id: item.subsId,
                msisdn: (
                    <LinkBlock url={RoutePath.subscriberId.replace(CLIENT_ID, item.subsId.toString())}>
                        {formatNumberToPhone(item.msisdn)}
                    </LinkBlock>
                ),
                delete: (
                    <div>
                        {removedSubscribers.includes(item.subsId) && (
                            <WizardStepConfirmationRemovedItemStyled style={{ position: 'absolute', left: 0, top: 0 }}>
                                <span>{stepTranslations.removedFromList}</span>
                                <LinkBlock onClick={() => recoverHandler(item.subsId)}>
                                    {stepTranslations.recoveryToList}
                                </LinkBlock>
                            </WizardStepConfirmationRemovedItemStyled>
                        )}
                        <ImgStyled
                            src={icons.removeSmallOpacityIcon}
                            width='24px'
                            onClick={() => removeHandler(item.subsId)}
                        />
                    </div>
                ),
            })),
        );
    }, [
        orderResult,
        recoverHandler,
        removeHandler,
        removedSubscribers,
        stepTranslations,
    ]);

    useEffect(() => {
        setTableColumns([
            {
                id: EWizardChangeRoleFieldList.MSISDN,
                title: stepTranslations.msisdn,
                width: 140,
            },
            {
                id: EWizardChangeRoleFieldList.DELETE,
                title: '',
                align: 'right',
                width: 40,
            },
        ]);
    }, [stepTranslations]);

    useEffect(() => {
        setSummaryParams([
            {
                title: summaryTranslations.subsCount,
                values: [`${subsCount}`],
            },
            {
                title: summaryTranslations.accessLevel,
                values: [`${selectedAccessLevel.def}`],
            },
        ]);
    }, [selectedAccessLevel, summaryTranslations, subsCount]);

    useEffect(() => {
        const listFiltered = orderResult.orderSubs.filter((item) => !removedSubscribers.includes(item.subsId));
        setSubsCount(listFiltered.length);
        setParams({
            subscriberIds: listFiltered.map((item) => item.subsId),
        });
        startValidate();
    }, [orderResult, removedSubscribers, setParams, startValidate]);

    return (
        <WizardConfirmationStep
            isReportSubscribers={true}
            startValidate={startValidate}
            storageSave={storageSave}
            summaryParams={summaryParams}
            summaryTitle=""
            tableColumns={tableColumns}
            tableData={tableData}
            wizardReport={translation.wizardReport}
        />
    );
};

export interface IConnectedProps
    extends Pick<IWizardChangeRoleReducer,
        'removedSubscribers' | 'orderResult' | 'selectedAccessLevel'> {
    translation: IWizardChangeRoleTranslation;
}

interface IDispatchProps {
    setParams: (payload: TWizardChangeRoleSetParamsPayload) => void;
    startValidate: () => void;
    storageSave: () => void;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    selectedAccessLevel: makeSelectWizardChangeRoleSelectedAccessLevel(),
    removedSubscribers: makeSelectWizardChangeRoleRemovedSubscribers(),
    orderResult: makeSelectWizardChangeRoleOrderResult(),
    translation: makeSelectTranslationWizardChangeRole(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setParams: (payload) => dispatch(wizardChangeRoleSetParamsAction(payload)),
    startValidate: () => dispatch(wizardChangeRoleStartValidateAction()),
    storageSave: () => dispatch(wizardReportStorageSaveAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardChangeRoleConfirmation);
